/* eslint-disable object-curly-newline */
import { call, put, select, takeEvery } from "redux-saga/effects";
import { toast } from "react-toastify";
import { handleError } from "helpers/HandleError";
import { getRequest, postRequest, putRequest, deleteRequest } from "../../helpers/Api";
import { types } from "../reducers/myProfile";
import { types as authTypes } from "../reducers/authentication";
import StorageManager from "../../helpers/StorageManager";
import { userNotAuthorizedHandler } from "./authentication";
import { types as formsTypes } from "../reducers/forms";

/**
 * Fetch students and lessons cound
 */
function* myProfileInitialFetch() {
  try {
    yield put({ type: types.SET_MY_PROFILE_LOADING, payload: { loading: false } });
  } catch (err) {
    toast.error(err);
  }
}

/**
 * Upload image and set user thumbnail
 * @param { file } payload
 */
function* uploadThumbnailRequest({ payload }) {
  yield put({ type: types.SET_MY_PROFILE_LOADING, payload: { loading: true } });
  const { user } = yield select((state) => state.authentication);
  const formData = new FormData();
  formData.append("file", payload.file);
  try {
    const uploaderRes = yield call(postRequest, {
      url: "/upload/avatar",
      uploader: true,
      body: formData,
      authorized: true,
    });

    if (uploaderRes.status === 401) {
      yield call(userNotAuthorizedHandler);
      yield put({ type: types.SET_MY_PROFILE_LOADING, payload: { loading: false } });
    }

    if (uploaderRes.status === 400) {
      yield put({
        type: types.SET_AVATAR_ERROR,
        payload: { error: uploaderRes.message || "Internal server error" },
      });
      yield put({ type: types.SET_MY_PROFILE_LOADING, payload: { loading: false } });
      return;
    }

    const userReq = yield call(putRequest, {
      url: "/profile/change-avatar",
      jsonContentType: true,
      authorized: true,
      body: JSON.stringify({ image: uploaderRes.image }),
    });

    if (userReq.status !== 200) {
      if (userReq.status === 401) {
        yield call(userNotAuthorizedHandler);
      }
      yield put({ type: types.SET_MY_PROFILE_LOADING, payload: { loading: false } });
      return;
    }

    const updatedUser = { ...user, image: userReq.image };

    yield put({ type: authTypes.SET_USER, payload: { user: updatedUser } });

    const checkToken = yield call(getRequest, {
      url: "/check-token",
      authorized: true,
    });

    if (checkToken.status === 200) {
      StorageManager.set("TOKEN", checkToken.token);
    } else if (checkToken.status === 401) {
      yield call(userNotAuthorizedHandler);
    }
    payload.onSuccess();
  } catch (error) {
    yield put({
      type: types.SET_AVATAR_ERROR,
      payload: { error: error.message || "Internal server error" },
    });
  }
  yield put({ type: types.SET_MY_PROFILE_LOADING, payload: { loading: false } });
}

function* deleteThumbnailRequest({ payload }) {
  yield put({ type: types.SET_MY_PROFILE_LOADING, payload: { loading: true } });
  const { user } = yield select((state) => state.authentication);

  try {
    const response = yield call(deleteRequest, {
      url: "/upload/avatar",
      uploader: true,
      authorized: true,
    });

    if (response.status === 401) {
      yield call(userNotAuthorizedHandler);
      yield put({ type: types.SET_MY_PROFILE_LOADING, payload: { loading: false } });
    }

    if (response.status === 400) {
      yield put({
        type: types.SET_AVATAR_ERROR,
        payload: {
          error: response.message,
        },
      });
      yield put({ type: types.SET_MY_PROFILE_LOADING, payload: { loading: false } });
      return;
    }

    const userReq = yield call(putRequest, {
      url: "/profile/change-avatar",
      jsonContentType: true,
      authorized: true,
      body: JSON.stringify({ image: "" }),
    });

    if (userReq.status !== 200) {
      if (userReq.status === 401) {
        yield call(userNotAuthorizedHandler);
      }
      yield put({ type: types.SET_MY_PROFILE_LOADING, payload: { loading: false } });
      return;
    }

    const updatedUser = { ...user, image: userReq.image };

    yield put({ type: authTypes.SET_USER, payload: { user: updatedUser } });

    const checkToken = yield call(getRequest, {
      url: "/check-token",
      authorized: true,
    });

    if (checkToken.status === 200) {
      StorageManager.set("TOKEN", checkToken.token);
    } else if (checkToken.status === 401) {
      yield call(userNotAuthorizedHandler);
    }
    payload.onSuccess();
  } catch (error) {
    yield put({
      type: types.SET_AVATAR_ERROR,
      payload: { error: error.message || "Internal server error" },
    });
  }
  yield put({ type: types.SET_MY_PROFILE_LOADING, payload: { loading: false } });
}

/**
 * Change profile password
 * @param { newPassword, oldPassword } payload
 */
function* changePasswordRequest({ payload }) {
  yield put({ type: types.SET_MY_PROFILE_LOADING, payload: { loading: true } });
  try {
    const changePassRes = yield call(putRequest, {
      url: "/profile/change-password",
      authorized: true,
      jsonContentType: true,
      body: JSON.stringify({
        oldPassword: payload.oldPassword,
        newPassword: payload.newPassword,
        confirmPassword: payload.confirmPassword,
      }),
    });
    if (changePassRes.status === 200) {
      payload.onSuccess();
    } else if (changePassRes.status === 401) {
      yield call(userNotAuthorizedHandler);
    } else if (changePassRes.status === 400) {
      if (changePassRes.response) {
        yield put({
          type: formsTypes.SET_VALIDATION_ERRORS,
          payload: {
            errors: [
              {
                key: changePassRes.response.field || "oldPassword",
                error: changePassRes.response.message || "Old password is not valid",
              },
            ],
          },
        });
      } else {
        yield put({
          type: formsTypes.SET_VALIDATION_ERRORS,
          payload: {
            errors: [{ key: "oldPassword", error: "Old password is not valid" }],
          },
        });
      }
    } else {
      handleError(changePassRes.message || "Internal server error");
    }
  } catch (err) {
    payload.onFailure(err.message || "Internal server error");
  }
  yield put({ type: types.SET_MY_PROFILE_LOADING, payload: { loading: false } });
}

export default function* saga() {
  yield takeEvery(types.MY_PROFILE_INITIAL_FETCH, myProfileInitialFetch);
  yield takeEvery(types.UPLOAD_THUMBNAIL_REQUEST, uploadThumbnailRequest);
  yield takeEvery(types.DELETE_THUMBNAIL_REQUEST, deleteThumbnailRequest);
  yield takeEvery(types.CHANGE_PASSWORD_REQUEST, changePasswordRequest);
}
